import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * DUPLICATES PAGE - Manage Duplicate Sites
 *
 * This page allows admins to view and merge duplicate sites that have
 * the same tenancy number and address combination.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PageHeader, PageHeaderWithBack, useToast } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { sitesService, } from "@/api/services/sites.service";
import { formatDateOnlyDisplay } from "@/utils/dateTime";
import styles from "./DuplicatesPage.module.css";
export const DuplicatesPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { success, error: toastError } = useToast();
    const [duplicates, setDuplicates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [merging, setMerging] = useState(null);
    const [selectedTargets, setSelectedTargets] = useState({});
    // Determine base path (admin or staff)
    const basePath = location.pathname.startsWith("/staff") ? "/staff" : "/admin";
    // Fetch duplicate sites
    const fetchDuplicates = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await sitesService.getDuplicateSites();
            setDuplicates(response.data.duplicates);
            // Initialize selected targets (default to first site in each group)
            const targets = {};
            response.data.duplicates.forEach((group) => {
                if (group.sites.length > 0) {
                    targets[group.key] = group.sites[0].id;
                }
            });
            setSelectedTargets(targets);
        }
        catch (err) {
            setError("Failed to load duplicate sites");
            console.error("Error fetching duplicates:", err);
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        fetchDuplicates();
    }, [fetchDuplicates]);
    // Handle merge
    const handleMerge = async (group) => {
        const targetSiteId = selectedTargets[group.key];
        if (!targetSiteId) {
            toastError("Please select a site to keep");
            return;
        }
        const sourceSiteIds = group.sites
            .filter((site) => site.id !== targetSiteId)
            .map((site) => site.id);
        if (sourceSiteIds.length === 0) {
            toastError("No sites to merge");
            return;
        }
        const confirmed = window.confirm(`Are you sure you want to merge ${sourceSiteIds.length} site(s) into the selected site? This will move all quotes and delete the duplicate sites. This action cannot be undone.`);
        if (!confirmed)
            return;
        setMerging(group.key);
        try {
            await sitesService.mergeDuplicateSites(targetSiteId, sourceSiteIds);
            success("Sites merged successfully");
            await fetchDuplicates();
        }
        catch (err) {
            console.error("Error merging sites:", err);
            toastError("Failed to merge sites. Please try again.");
        }
        finally {
            setMerging(null);
        }
    };
    // Handle target selection
    const handleTargetChange = (groupKey, siteId) => {
        setSelectedTargets((prev) => ({
            ...prev,
            [groupKey]: siteId,
        }));
    };
    return (_jsxs("div", { className: styles.container, children: [_jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }), _jsx(PageHeaderWithBack, { title: "Manage Duplicate Sites", onBack: () => navigate(`${basePath}/sites`) }), loading && (_jsx("div", { className: styles.loading, children: _jsx("p", { children: "Loading duplicate sites..." }) })), error && (_jsxs("div", { className: styles.error, children: [_jsx("p", { children: error }), _jsx("button", { onClick: fetchDuplicates, className: styles.retryBtn, children: "Retry" })] })), !loading && !error && duplicates.length === 0 && (_jsxs("div", { className: styles.emptyState, children: [_jsx("div", { className: styles.emptyIcon, children: _jsx("svg", { width: "64", height: "64", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: _jsx("path", { d: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" }) }) }), _jsx("h3", { children: "No Duplicates Found" }), _jsx("p", { children: "All your sites are unique. Great job keeping things organized!" }), _jsx("button", { className: styles.backBtn, onClick: () => navigate(`${basePath}/sites`), children: "Back to Sites" })] })), !loading && !error && duplicates.length > 0 && (_jsxs("div", { className: styles.duplicatesList, children: [_jsxs("div", { className: styles.header, children: [_jsxs("h2", { className: styles.title, children: ["Found ", duplicates.length, " Duplicate Group", duplicates.length !== 1 ? "s" : ""] }), _jsx("p", { className: styles.subtitle, children: "Review and merge duplicate sites to keep your data clean. Select which site to keep, and all quotes will be moved to it." })] }), duplicates.map((group) => (_jsxs("div", { className: styles.duplicateGroup, children: [_jsxs("div", { className: styles.groupHeader, children: [_jsxs("div", { className: styles.groupInfo, children: [_jsxs("h3", { className: styles.groupTitle, children: [group.tenancyNumber && `#${group.tenancyNumber} - `, group.address || "No Address"] }), _jsxs("span", { className: styles.siteCount, children: [group.sites.length, " sites \u2022 ", group.quoteCount, " quote", group.quoteCount !== 1 ? "s" : ""] })] }), _jsx("button", { className: styles.mergeBtn, onClick: () => handleMerge(group), disabled: merging === group.key, children: merging === group.key ? "Merging..." : "Merge Sites" })] }), _jsx("div", { className: styles.sitesGrid, children: group.sites.map((site) => (_jsxs("div", { className: `${styles.siteCard} ${selectedTargets[group.key] === site.id
                                        ? styles.selected
                                        : ""}`, onClick: () => handleTargetChange(group.key, site.id), children: [_jsx("div", { className: styles.radioWrapper, children: _jsx("input", { type: "radio", name: `target-${group.key}`, checked: selectedTargets[group.key] === site.id, onChange: () => handleTargetChange(group.key, site.id), className: styles.radio }) }), _jsxs("div", { className: styles.siteInfo, children: [_jsx("h4", { className: styles.siteName, children: site.siteName }), site.tenancyName && (_jsxs("p", { className: styles.siteDetail, children: ["Tenancy: ", site.tenancyName] })), site.suburb && (_jsxs("p", { className: styles.siteDetail, children: ["Suburb: ", site.suburb] })), site.companyName && (_jsxs("p", { className: styles.siteDetail, children: ["Company: ", site.companyName] })), _jsxs("p", { className: styles.siteDate, children: ["Created: ", formatDateOnlyDisplay(site.createdAt)] })] }), selectedTargets[group.key] === site.id && (_jsx("div", { className: styles.keepBadge, children: "Keep" }))] }, site.id))) })] }, group.key)))] }))] }));
};
